import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Slider } from 'primereact/slider';
import { FileUpload } from 'primereact/fileupload';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { AutoComplete } from 'primereact/autocomplete';
import { useGetHostGroupsQuery } from '../../api/groupApi';
import { useCreateGiveawayMutation, useLazyGetAddressSuggestionsQuery } from '../../api/giveawayApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import EntityFormPage from '../../components/EntityFormPage/EntityFormPage';
import buttons from '../../styles/shared/buttons.module.css';
import styles from './GiveawayCreatePage.module.css';

const ALL_ALLERGENS = [
  { label: 'peanuts', value: 'peanuts' },
  { label: 'tree nuts', value: 'tree nuts' },
  { label: 'dairy', value: 'dairy' },
  { label: 'eggs', value: 'eggs' },
  { label: 'gluten', value: 'gluten' },
  { label: 'soy', value: 'soy' },
  { label: 'shellfish', value: 'shellfish' },
  { label: 'fish', value: 'fish' },
  { label: 'sesame', value: 'sesame' },
];

const MAX_PHOTOS = 10;
const MAX_PHOTO_SIZE = 5 * 1024 * 1024;
const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export default function GiveawayCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();

  const { data: groups } = useGetHostGroupsQuery();
  const [createGiveaway, { isLoading: isCreating }] = useCreateGiveawayMutation();
  const [triggerAddressSuggestions] = useLazyGetAddressSuggestionsQuery();
  const [photos, setPhotos] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      groupId: location.state?.groupId ?? '',
      title: '',
      location: '',
      radiusMiles: 1,
      food: '',
      servingsLeft: null,
      allergens: [],
    },
  });
  const groupId = watch('groupId');
  const selectedGroup = groups?.find((g) => g.id === groupId);
  const geolocationRequired = Boolean(selectedGroup?.geolocation_required);

  function handleSelectPhotos(e) {
    const incoming = e.files.filter((file) => {
      if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
        showToast('error', 'Giveaways', `${file.name}: only jpeg, png, webp, or gif images are allowed.`);
        return false;
      }
      if (file.size > MAX_PHOTO_SIZE) {
        showToast('error', 'Giveaways', `${file.name}: images must be 5 MB or smaller.`);
        return false;
      }
      return true;
    });
    setPhotos((prev) => [...prev, ...incoming].slice(0, MAX_PHOTOS));
  }

  function removePhoto(index) {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  }

  async function searchAddress(e) {
    const q = e.query.trim();
    if (q.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    const results = await triggerAddressSuggestions(q)
      .unwrap()
      .catch((err) => {
        console.error('Address suggestion lookup failed:', err);
        return [];
      });
    setAddressSuggestions(results.map((r) => r.display_name));
  }

  const onCreate = async (data) => {
    try {
      await createGiveaway({
        groupId: data.groupId,
        title: data.title.trim(),
        location: data.location.trim(),
        radiusMiles: geolocationRequired ? data.radiusMiles : undefined,
        food: data.food.trim() || null,
        servingsLeft: data.servingsLeft ?? undefined,
        allergens: data.allergens,
        photoFiles: photos,
      }).unwrap();
      showToast('success', 'Giveaways', 'Giveaway created.');
      navigate('/host/giveaways');
    } catch (err) {
      showToast('error', 'Giveaways', errMsg(err, 'Could not create giveaway.'));
    }
  };

  return (
    <EntityFormPage title="Add giveaway" backTo="/host/giveaways">
      <form onSubmit={handleSubmit(onCreate)}>
        <Controller
          name="groupId"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Group</label>
              <Dropdown
                id={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                options={groups?.map((g) => ({ label: g.name, value: g.id }))}
                className="w-full mb-3"
              />
            </div>
          )}
        />

        <Controller
          name="title"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Title</label>
              <InputText
                id={field.name}
                className="w-full mb-3"
                placeholder="Pizza Station"
                maxLength={200}
                {...field}
              />
            </div>
          )}
        />

        <Controller
          name="location"
          control={control}
          rules={{ required: true }}
          render={({ field }) =>
            geolocationRequired ? (
              <div className="field">
                <label htmlFor={field.name}>Location</label>
                <AutoComplete
                  id={field.name}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  onBlur={field.onBlur}
                  suggestions={addressSuggestions}
                  completeMethod={searchAddress}
                  delay={400}
                  placeholder="Start typing a full street address…"
                  maxLength={300}
                  className="w-full mb-3"
                  inputClassName="w-full"
                  appendTo="self"
                  panelStyle={{ width: '100%' }}
                  itemTemplate={(suggestion) => (
                    <span
                      style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={suggestion}
                    >
                      {suggestion}
                    </span>
                  )}
                />
                <p className="textMuted mb-3">
                  This group requires geolocation — pick a suggested address so it can be
                  located precisely to check subscriber distance.
                </p>
              </div>
            ) : (
              <div className="field">
                <label htmlFor={field.name}>Location</label>
                <InputText
                  id={field.name}
                  className="w-full mb-3"
                  placeholder="Student Union, 2nd floor"
                  maxLength={300}
                  {...field}
                />
              </div>
            )
          }
        />

        {geolocationRequired && (
          <Controller
            name="radiusMiles"
            control={control}
            render={({ field }) => (
              <div className="field mb-3">
                <label>
                  Notification radius:{' '}
                  <span
                    style={{
                      display: 'inline-block',
                      minWidth: '4ch',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {field.value}
                  </span>{' '}
                  miles
                </label>
                <Slider
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  min={0}
                  max={25}
                  step={0.5}
                  className="w-full"
                />
              </div>
            )}
          />
        )}

        <Controller
          name="food"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Food</label>
              <div className={`${styles.roundedTextarea} mb-3`}>
                <InputTextarea
                  id={field.name}
                  className="w-full"
                  rows={3}
                  placeholder="Cheese pizza (optional)"
                  maxLength={256}
                  {...field}
                />
              </div>
            </div>
          )}
        />

        <Controller
          name="servingsLeft"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Servings left</label>
              <InputNumber
                inputId={field.name}
                className="w-full mb-3"
                inputClassName="w-full"
                value={field.value}
                onValueChange={(e) => field.onChange(e.value)}
                min={0}
                useGrouping={false}
                placeholder="Optional"
              />
            </div>
          )}
        />

        <Controller
          name="allergens"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Allergens</label>
              <MultiSelect
                id={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                options={ALL_ALLERGENS}
                className="w-full mb-3"
                display="chip"
                showSelectAll={false}
                placeholder="Select allergens (optional)"
              />
            </div>
          )}
        />

        <div className="field">
          <label>Photos</label>
          <FileUpload
            mode="basic"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif"
            auto={false}
            chooseLabel="Choose photos"
            customUpload
            uploadHandler={() => {}}
            onSelect={handleSelectPhotos}
            disabled={photos.length >= MAX_PHOTOS}
            className="mb-3"
          />
          {photos.length > 0 && (
            <div className="mb-3" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {photos.map((file, index) => (
                <Chip key={`${file.name}-${index}`} label={file.name} removable onRemove={() => removePhoto(index)} />
              ))}
            </div>
          )}
          <p className="textMuted">Up to {MAX_PHOTOS} images, 5 MB each (optional).</p>
        </div>

        <Button type="submit" label="Save" className={buttons.saveBtn} loading={isCreating} />
      </form>
    </EntityFormPage>
  );
}
